import { registerNetworkStatusCallback } from '@bilderit/offline-link';
import { capture } from './capture';
import { flushAnalyticsQueue } from './queue';

/**
 * Session-level connectivity summary — the other client-unique signal this
 * plan adds (alongside `was_offline` on mutation_completed). A flaky
 * connection can flap for hours; emitting one event per transition would
 * dominate volume for no extra insight, so this aggregates instead.
 *
 * Reuses createOfflineLink's threshold/debounce values (OfflineLink.ts:26-31)
 * so "offline" means the same thing here as it does for the mutation queue.
 */
const CONNECTIVITY_THRESHOLD = '3g';
const CONNECTIVITY_DEBOUNCE_MS = 2000;
const FLUSH_INTERVAL_MS = 5 * 60 * 1000;

let transitionCount = 0;
let offlineMs = 0;
let offlineSince: number | undefined;
let ignoredInitialInvocation = false;

const flush = (): void => {
  // A still-open offline interval (flushed mid-outage) is accrued up to
  // now and the interval restarted, so a long outage is reported as it
  // happens — one slice per summary — rather than only once it ends (and
  // never, for a session that starts offline and closes before it ends).
  if (offlineSince != null) {
    const now = Date.now();
    offlineMs += now - offlineSince;
    offlineSince = now;
  }
  if (transitionCount === 0 && offlineMs === 0) return;
  try {
    capture('connectivity_summarized', {
      transition_count: transitionCount,
      offline_ms: offlineMs,
    });
  } catch {
    // A connectivity summary must never affect the app.
  }
  transitionCount = 0;
  offlineMs = 0;
};

const handleConnectivityChange = (isConnected: boolean): void => {
  // Reuses this registration (one listener, two consumers) to also drain
  // the offline analytics queue on the online edge, rather than registering
  // a second registerNetworkStatusCallback listener — see queue.ts. This
  // also covers the initial synchronous invocation below (already online at
  // registration), which is harmless: flushAnalyticsQueue() is a cheap
  // no-op when the queue is empty.
  if (isConnected) {
    void flushAnalyticsQueue();
  }

  // registerNetworkStatusCallback invokes the callback once synchronously
  // with the current state before any real transition — that first call
  // must not count as a "transition".
  if (!ignoredInitialInvocation) {
    ignoredInitialInvocation = true;
    if (!isConnected) offlineSince = Date.now();
    return;
  }

  transitionCount += 1;
  if (!isConnected) {
    offlineSince = Date.now();
  } else if (offlineSince != null) {
    offlineMs += Date.now() - offlineSince;
    offlineSince = undefined;
  }
};

/**
 * Wires the connectivity summary. Call once, only from initPostHog()'s
 * success path — nothing is registered (and no listeners attached) when
 * PostHog was never initialised (no VITE_APP_POSTHOG_KEY).
 */
export const startConnectivitySummary = (): void => {
  registerNetworkStatusCallback(
    CONNECTIVITY_THRESHOLD,
    handleConnectivityChange,
    CONNECTIVITY_DEBOUNCE_MS
  );

  setInterval(flush, FLUSH_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    // 'hidden', not 'unload' — unload is unreliable on mobile Safari.
    if (document.visibilityState === 'hidden') flush();
  });
};
